import React, { useContext, useEffect, useState } from 'react';
import i18n from 'i18n-js';
import * as Yup from 'yup';
import {
  powerUnits,
  rangeUnits,
  fuelCapacityUnits,
  displacementUnits,
  powertrainOptionalValues,
} from 'constants/vehicleTypes';
import { cloneDeep } from 'lodash';
import PropTypes from 'prop-types';
import { UserContext } from 'context';
import { Formik, Field } from 'formik';
import { getPowertrains } from 'utils/utils';
import { handleShowError, handleError } from 'utils';
import { useDispatch, useSelector } from 'react-redux';
import { vehicleActions } from 'state/modules/vehicles';
import { Box, Grid, Container, Typography } from '@mui/material';
import { TextField, TextareaField, Autocomplete, MaterialSelectField } from 'components/FormFields';
import { VehiclesTypesCategoriesSelect, VehiclesAssetClassesSelect } from 'components/AsyncFields';

export const VehiclesTypesWizardDetails = ({ formRef, vehicleType, errors: asyncErrors }) => {
  const dispatch = useDispatch();
  const addNewCategoryValue = i18n.t('vehicle_types.add_new_category');
  const { orgType } = useContext(UserContext);
  const organizationId = useSelector((state) => state.auth?.user?.organization?.id);
  const [powertrainValue, setPowertrainValue] = useState(vehicleType?.powertrain);
  const [showAssetClassSelect, setShowAssetClassSelect] = useState(true);

  useEffect(() => {
    setPowertrainValue(vehicleType?.powertrain);
  }, [vehicleType]);

  const formSchema = Yup.object().shape({
    year: Yup.string()
      .matches(/^[0-9]+$/, i18n.t('common.only_digits'))
      .min(4, i18n.t('common.max_value_4'))
      .max(4, i18n.t('common.max_value_4'))
      .required(i18n.t('common.required_field')),
    make: Yup.string().required(i18n.t('common.required_field')),
    model: Yup.string().required(i18n.t('common.required_field')),

    trim: Yup.string(),

    categorySelect: Yup.string().required(i18n.t('common.required_field')),

    categoryInput: Yup.string().when('categorySelect', {
      is: addNewCategoryValue,
      then: Yup.string().max(32).required(i18n.t('common.required_field')),
    }),

    powertrain: Yup.string().required(i18n.t('common.required_field')),
    asset_class: Yup.string().required(i18n.t('common.required_field')),
    msrp: Yup.number(),

    power: Yup.object().shape({
      magnitude: Yup.number(),
      units: Yup.string(),
    }),

    range: Yup.object().shape({
      magnitude: Yup.number(),
      units: Yup.string(),
    }),

    description: Yup.string(),

    fuel_capacity: Yup.object().shape({
      magnitude: powertrainOptionalValues.includes(powertrainValue)
        ? Yup.number().required(i18n.t('common.required_field'))
        : Yup.string(),
      units: powertrainOptionalValues.includes(powertrainValue)
        ? Yup.string().required(i18n.t('common.required_field'))
        : Yup.string(),
    }),

    displacement: Yup.object().shape({
      magnitude: Yup.number(),
      units: Yup.string(),
    }),

    cylinders: Yup.number(),

    engine_type: Yup.string(),
  });
  const disabledField = orgType === 'msServiceOrg' ? true : false;

  const initialValues = {
    display_name: vehicleType?.display_name || '',
    year: vehicleType?.year || '',
    make: vehicleType?.make || '',
    model: vehicleType?.model || '',

    categorySelect: vehicleType?.category || '',
    categoryInput: '',

    trim: vehicleType?.trim || '',
    powertrain: vehicleType?.powertrain || '',
    asset_class: vehicleType?.asset_class || '',
    msrp: vehicleType?.msrp || '',
    power: vehicleType?.power || { magnitude: '', units: '', display_name: '' },
    range: vehicleType?.range || { magnitude: '', units: '', display_name: '' },
    fuel_capacity: vehicleType?.fuel_capacity || { magnitude: '', units: '', display_name: '' },
    displacement: vehicleType?.displacement || { magnitude: '', units: '', display_name: '' },
    cylinders: vehicleType?.cylinders || '',
    engine_type: vehicleType?.engine_type || '',
    description: vehicleType?.description || '',
  };

  const onSubmit = (values) => {
    let data = cloneDeep(values);

    // remove empty fields
    const removeEmpty = (data) => {
      Object.keys(data).forEach((key) => {
        if (data[key] instanceof Object) {
          removeEmpty(data[key]);
          !Object.keys(data[key]).length && delete data[key];
        }
        if (key === 'msrp' && !data[key] && vehicleType?.id) {
          return (data[key] = null);
        }
        !data[key] && delete data[key];
      });
    };
    removeEmpty(data);

    // category validation
    // remove aux fields and set category field
    data.category =
      data.categorySelect === addNewCategoryValue ? data.categoryInput : data.categorySelect;
    delete data.categorySelect;
    delete data.categoryInput;

    if (data?.range?.units && !data?.range?.magnitude) {
      data.range = null;
    }

    if (data?.power?.units && !data?.power?.magnitude) {
      data.power = null;
    }

    if (data?.fuel_capacity?.units && !data?.fuel_capacity?.magnitude) {
      data.fuel_capacity = null;
    }

    if (data?.displacement?.units && !data?.displacement?.magnitude) {
      data.displacement = null;
    }

    const payload = {
      ...data,
      organization_id: organizationId,
    };

    // remove extra fields depending powertrain
    if (!powertrainOptionalValues.includes(values?.powertrain)) {
      delete payload?.fuel_capacity;
      delete payload?.displacement;
      delete payload?.cylinders;
      delete payload?.engine_type;
    }

    if (vehicleType?.id) {
      return dispatch(vehicleActions.updateVehicleType(vehicleType?.id, payload));
    }

    dispatch(vehicleActions.createVehicleType(payload));
  };

  const PowertrainOptional = ({ value }) =>
    powertrainOptionalValues.includes(value) ? i18n.t('common.optional') : '';

  const findPowertrain = (value) => {
    const powertrain = getPowertrains()?.find((pt) => pt.value === value);
    return powertrain?.label || null;
  };

  const EngineSpecsSection = ({ values }) => (
    <>
      <Grid
        size={{ xs: 12 }}
        sx={{
          paddingBottom: 0,
        }}
      >
        <Typography variant="h6">
          <Box fontWeight="500" fontSize={14} mt={8}>
            {i18n.t('vehicle_types.engine_specs')}
          </Box>
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 2 }}>
        <Field
          type="number"
          name="fuel_capacity.magnitude"
          disabled={disabledField}
          required={true}
          component={TextField}
          asyncErrors={asyncErrors?.fuel_capacity}
          label="Fuel capacity"
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 1 }}>
        <Field
          type="text"
          name="fuel_capacity.units"
          disabled={disabledField}
          required={true}
          style={{ marginTop: 30 }}
          component={MaterialSelectField}
          options={setDefaultValue(fuelCapacityUnits)?.map((p) => {
            return { value: p, label: p };
          })}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 2 }}>
        <Field
          type="number"
          name="displacement.magnitude"
          disabled={disabledField}
          required={false}
          component={TextField}
          asyncErrors={asyncErrors?.displacement}
          label="Displacement"
        />
        <PowertrainOptional value={values?.powertrain} />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 1 }}>
        <Field
          type="text"
          name="displacement.units"
          disabled={disabledField}
          style={{ marginTop: 30 }}
          component={MaterialSelectField}
          options={setDefaultValue(displacementUnits)?.map((p) => {
            return { value: p, label: p };
          })}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Field
          name="cylinders"
          type="number"
          disabled={disabledField}
          component={TextField}
          required={false}
          label="Cylinders"
        />
        <PowertrainOptional value={values?.powertrain} />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Field
          name="engine_type"
          disabled={disabledField}
          component={TextField}
          required={false}
          label="Engine type"
        />
        <PowertrainOptional value={values?.powertrain} />
      </Grid>
    </>
  );

  const setDefaultValue = (array = [], value = '') => {
    return [value, ...array];
  };

  return (
    <Box
      maxWidth="100%"
      sx={{
        '& input': {
          fontSize: (theme) => theme.typography.appFontSize,
        },
      }}
    >
      <Container className="h-100">
        <Grid
          container
          rowSpacing={1}
          direction="row"
          alignItems="center"
          justifyContent="center"
          columnSpacing={{ xs: 1, sm: 2, md: 3 }}
        >
          <Grid size={{ xs: 11 }}>
            <Formik
              innerRef={formRef}
              initialValues={initialValues}
              validationSchema={formSchema}
              enableReinitialize={true}
              onSubmit={onSubmit}
            >
              {({ handleSubmit, setFieldValue, validateForm, values, errors, touched }) => (
                <form noValidate onSubmit={handleSubmit}>
                  <Grid
                    container
                    rowSpacing={1}
                    direction="row"
                    alignItems="flex-start"
                    justifyContent="flex-start"
                    columnSpacing={{ xs: 1, sm: 2, md: 3 }}
                    className={disabledField ? 'disabled' : ''}
                  >
                    <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                      <Field
                        rows={2}
                        type="text"
                        name="description"
                        required={false}
                        variant="standard"
                        label="Description"
                        disabled={disabledField}
                        component={TextareaField}
                        asyncErrors={asyncErrors?.description}
                      />
                      {i18n.t('common.optional')}
                    </Grid>
                    <Grid
                      size={{ xs: 12 }}
                      style={{
                        paddingBottom: 0,
                      }}
                    >
                      <Typography variant="h6">
                        <Box fontWeight="500" fontSize={14} mt={8}>
                          {i18n.t('vehicle_types.quick_specs')}
                        </Box>
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Field
                        name="year"
                        component={TextField}
                        required={true}
                        label={i18n.t('vehicle_types.year')}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Field
                        name="make"
                        disabled={disabledField}
                        component={TextField}
                        required={true}
                        label="Make"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Field
                        name="model"
                        component={TextField}
                        disabled={disabledField}
                        required={true}
                        label="Model"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Field
                        name="trim"
                        component={TextField}
                        disabled={disabledField}
                        required={false}
                        label="TRIM"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Field
                        name="categorySelect"
                        component={VehiclesTypesCategoriesSelect}
                        disabled={disabledField}
                        required={true}
                        label="Category"
                        customParams={{ keyword: vehicleType?.category }}
                      />
                    </Grid>
                    {values.categorySelect === addNewCategoryValue && (
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Field
                          name="categoryInput"
                          disabled={disabledField}
                          component={TextField}
                          required={true}
                          label={addNewCategoryValue}
                        />
                      </Grid>
                    )}

                    <Grid
                      size={{ xs: 12 }}
                      sx={{
                        paddingBottom: 0,
                      }}
                    >
                      <Typography variant="h6">
                        <Box fontWeight="500" fontSize={14} mt={8}>
                          {i18n.t('vehicle_types.performance_specs')}
                        </Box>
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Field
                        id="vehicleTypePowertrain"
                        name="powertrain"
                        label="Powertrain"
                        required={true}
                        options={getPowertrains()}
                        disabled={disabledField}
                        component={Autocomplete}
                        value={findPowertrain(values?.powertrain)}
                        getOptionLabel={(option) => option?.label || option || ''}
                        error={handleError('powertrain', errors, touched)}
                        helperText={handleShowError('powertrain', errors, touched)}
                        onChange={(_, val) => {
                          setFieldValue('powertrain', val?.value);
                          setPowertrainValue(val?.value);
                          setTimeout(() => {
                            validateForm();
                          }, 100);
                        }}
                        getOptionSelected={(option, value) =>
                          option?.value === value || option?.label === value
                        }
                      />
                    </Grid>
                    {showAssetClassSelect && (
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Field
                          name="asset_class"
                          disabled={disabledField}
                          component={VehiclesAssetClassesSelect}
                          customParams={{ keyword: values?.asset_class }}
                          label="Asset class"
                          required={true}
                          onHide={() => setShowAssetClassSelect(false)}
                        />
                      </Grid>
                    )}
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Field
                        name="msrp"
                        type="number"
                        disabled={disabledField}
                        required={false}
                        component={TextField}
                        asyncErrors={asyncErrors?.msrp}
                        label="MSRP"
                      />
                      {i18n.t('common.optional')}
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                      <Field
                        type="number"
                        name="power.magnitude"
                        disabled={disabledField}
                        required={false}
                        component={TextField}
                        asyncErrors={asyncErrors?.power}
                        label="Power"
                      />
                      {i18n.t('common.optional')}
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 1 }}>
                      <Field
                        type="text"
                        name="power.units"
                        disabled={disabledField}
                        style={{ marginTop: 30 }}
                        component={MaterialSelectField}
                        options={setDefaultValue(powerUnits)?.map((p) => {
                          return { value: p, label: p };
                        })}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                      <Field
                        type="number"
                        name="range.magnitude"
                        disabled={disabledField}
                        component={TextField}
                        required={false}
                        asyncErrors={asyncErrors?.range}
                        label="Range"
                      />
                      {i18n.t('common.optional')}
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 1 }}>
                      <Field
                        type="text"
                        name="range.units"
                        disabled={disabledField}
                        style={{ marginTop: 30 }}
                        component={MaterialSelectField}
                        options={setDefaultValue(rangeUnits)?.map((p) => {
                          return { value: p, label: p };
                        })}
                      />
                    </Grid>
                    {powertrainOptionalValues.includes(powertrainValue) && (
                      <EngineSpecsSection values={values} />
                    )}
                  </Grid>
                </form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

VehiclesTypesWizardDetails.propTypes = {
  formRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  vehicleType: PropTypes.object,
  errors: PropTypes.object,
  values: PropTypes.object,
};
